import { useSyncExternalStore } from 'react'

/**
 * The current minute — and the current second — as values a component re-renders on.
 *
 * Three things on the customer's screen are true only until a clock ticks past them: **whether the
 * restaurant is open**, **whether lunch is being served**, and **how long until the wheel can be spun
 * again**. All are computed from `new Date()` at render, which is right — and none causes a render, which
 * is not. A customer sitting on the menu at 09:44 watches it say *closed* until they touch something.
 *
 * The C0 spec names the second case in as many words: *"`checkLunchTimeActive` keeps its 60-second
 * interval — a lunch window that opens while somebody is looking at the page should open on the page."*
 * The third is legacy's `Countdown`, which ticks every second (§14.1: copy every interval exactly).
 *
 * **One timer for the whole app, not one per component and not one per granularity.** The interval was
 * already running at 1 s to notice the minute turning, so the second-by-second readers ride the same tick
 * rather than starting a second one. Every subscriber is woken by the same timer, adding a reader costs
 * nothing, and there is no interval left running by a component that unmounted mid-tick.
 *
 * `useSyncExternalStore` rather than `useState` + `setInterval`: the clock is a value outside React that
 * components must stay in step with, which is what the hook is for — and it keeps the shell's own lint
 * rule about setState-in-effect satisfied without an exception.
 *
 * **Each granularity wakes only its own readers.** A menu that re-rendered every second under a reading
 * thumb would be the cure being worse than the complaint.
 */

const MINUTE = 60_000
const SECOND = 1_000

type Grain = 'minute' | 'second'

const listeners: Record<Grain, Array<() => void>> = { minute: [], second: [] }
const snapshots: Record<Grain, number> = {
  minute: Math.floor(Date.now() / MINUTE),
  second: Math.floor(Date.now() / SECOND),
}
const sizes: Record<Grain, number> = { minute: MINUTE, second: SECOND }

let timer: ReturnType<typeof setInterval> | null = null

function tick(): void {
  const now = Date.now()
  for (const grain of ['minute', 'second'] as const) {
    const next = Math.floor(now / sizes[grain])
    // Only when this grain actually turns. A tick that changes nothing must not re-render the menu.
    if (next === snapshots[grain]) continue
    snapshots[grain] = next
    for (const listener of listeners[grain]) listener()
  }
}

function running(): boolean {
  return listeners.minute.length > 0 || listeners.second.length > 0
}

function subscriber(grain: Grain) {
  return (onChange: () => void): (() => void) => {
    listeners[grain] = [...listeners[grain], onChange]
    if (timer === null) timer = setInterval(tick, SECOND)
    return () => {
      listeners[grain] = listeners[grain].filter((entry) => entry !== onChange)
      if (!running() && timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }
  }
}

const subscribeMinute = subscriber('minute')
const subscribeSecond = subscriber('second')

/**
 * Re-renders the caller once a minute.
 *
 * The value is the minute number and is not meant to be read — a component that needs the time reads
 * `new Date()`, which is now correct because this made it render again.
 */
export function useMinute(): number {
  return useSyncExternalStore(
    subscribeMinute,
    () => snapshots.minute,
    // No server render here; the clock at module load is as good an answer as any.
    () => snapshots.minute,
  )
}

/**
 * The current time in epoch milliseconds, floored to the second, re-rendering the caller as it changes.
 *
 * For a running countdown and nothing else. A screen that only needs to know *whether* a moment has
 * passed wants {@link useMinute}; this is for one that shows the seconds going by.
 *
 * Unlike {@link useMinute} this **returns the time**, because its callers need it: reading `Date.now()`
 * during render is impure and the shell's lint rule rejects it, so the clock is handed to the component
 * rather than fetched by it. Flooring to the second is not a loss — a countdown displays whole seconds.
 */
export function useSecond(): number {
  const second = useSyncExternalStore(
    subscribeSecond,
    () => snapshots.second,
    () => snapshots.second,
  )
  return second * SECOND
}
